import { useAppDispatch } from '@/app/hooks';
import pqLogoColor from '@/assets/pq_logo_color.png';
import { setSelectedMenuItem } from '@/components/Menu/menuSlice';
import Notifications from '@/components/Notifications';
import { useAccount } from '@/hooks/useAccount';
import { ActionIcon, Anchor, Badge, Box, Button, Drawer, Group, Image, Stack, Text, Title } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconSettings, IconUser } from '@tabler/icons-react';
import { useAuth } from '@workos-inc/authkit-react';
import { useNavigate } from 'react-router';
import { useRef, useState } from 'react';

const USER_GUIDE_URL = 'https://pqweb.parcelquest.com/Content/Help/pdf/PQMobileUserGuide.pdf';

const SideNav: React.FC = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { account: pqUser, signOut } = useAccount();
    const displayName = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || 'User';
    const [signingOut, setSigningOut] = useState(false);
    const [logoutError, setLogoutError] = useState<string | null>(null);
    const logoutPending = useRef(false);
    const [sideNavOpened, sideNavHandlers] = useDisclosure(false);
    const [notificationsOpened, notificationsHandlers] = useDisclosure(false);

    const openSettingsHandler = () => {
        dispatch(setSelectedMenuItem('closed'));
        sideNavHandlers.toggle();
    }
    const onLogoutHandler = async () => {
        if (logoutPending.current) return;
        logoutPending.current = true;
        setSigningOut(true);
        setLogoutError(null);
        try {
            await signOut();
        } catch {
            setLogoutError('Unable to sign out. Please try again.');
        } finally {
            logoutPending.current = false;
            setSigningOut(false);
        }
    }

    return (
        <>
            <ActionIcon variant='transparent' onClick={openSettingsHandler} size='md' aria-label='Open Settings '>
                <IconSettings color='white' />
            </ActionIcon>
            <Drawer
                opened={sideNavOpened}
                onClose={sideNavHandlers.toggle}
                withCloseButton={false}
                position='left'
                padding='lg'
                autoFocus={false}
                trapFocus={false}
                styles={{ content: { maxWidth: '75vw' }, body: { height: '100%'  }}}
            >
                <Stack justify='space-between' align='space-between' style={{ height: '100%' }}>
                    <Stack justify='center' align='start' gap={0}>
                        <Group align='center' mb={10} wrap='nowrap' w='100%'>
                            <Badge circle color='pq-dark-gray' size='40px' style={{ flexShrink: 0 }}>
                                <IconUser size='34' color='white' />
                            </Badge>
                            {/* Long names wrap to two lines, then truncate; the full name stays available on hover/long-press. */}
                            <Title order={3} lineClamp={2} title={displayName} style={{ minWidth: 0, overflowWrap: 'anywhere' }}>{displayName}</Title>
                        </Group>
                        <Button fullWidth size='lg' variant='transparent' color="pq-red" p={0} styles={{ inner: { justifyContent: 'start' } }} onClick={() => notificationsHandlers.toggle()}>Notifications</Button>
                        {pqUser.canManageBilling ? <Button fullWidth size='lg' variant='transparent' color="pq-red" p={0} styles={{ inner: { justifyContent: 'start' } }} onClick={() => navigate('/billing')}>Payment & billing</Button> : null}
                        {/* A new browsing context, so installed apps open the PDF outside the app. */}
                        <Button component='a' href={USER_GUIDE_URL} target='_blank' rel='noopener noreferrer' fullWidth size='lg' variant='transparent' color="pq-red" p={0} styles={{ inner: { justifyContent: 'start' } }}>User Guide</Button>
                        <Button fullWidth size='lg' variant='transparent' color="pq-red" p={0} styles={{ inner: { justifyContent: 'start' } }} loading={signingOut} onClick={onLogoutHandler}>Logout</Button>
                        {logoutError && <Text c="red" role="alert">{logoutError}</Text>}
                    </Stack>
                    <Stack>
                        <Stack gap={3}>
                            <Text fw={600} c="pq-red" size='lg'>support:</Text>
                            <Anchor fw={600} c='black' size='md' underline='never' href='tel:18337681224'>(833) 768-1224</Anchor>
                            <Anchor fw={600} c='black' size='md' underline='never' href='mailto:mobilesupport@parcelquest.com?subject=PQMobile Support Request&body=Please describe your issue out below:'>mobilesupport@parcelquest.com</Anchor>
                            <Box>
                                <Image fit='contain' src={pqLogoColor} alt="ParcelQuest Logo" style={{ width: '55%', marginTop: 10, marginBottom: 10 }} />
                            </Box>
                            <Text fw={600} mb={10} c="pq-red">Version:&nbsp;{import.meta.env.VITE_APP_VERSION}</Text>
                        </Stack>
                    </Stack>
                </Stack>
                <Notifications opened={notificationsOpened} onClose={notificationsHandlers.toggle} />
            </Drawer>
        </>
    );
}

export default SideNav;
